<template>
  <el-dialog
    v-model="dialogVisible"
    title="修改密码"
    width="400px"
    center
    destroy-on-close
    :append-to-body="true"
    class="change-password-dialog"
  >
    <el-form
      ref="formRef"
      :model="form"
      :rules="rules"
      label-width="100px"
      @submit.prevent="submitForm"
    >
      <el-form-item label="当前密码" prop="currentPassword">
        <el-input
          v-model="form.currentPassword"
          type="password"
          placeholder="请输入当前密码"
          show-password
        />
      </el-form-item>
      
      <el-form-item label="新密码" prop="newPassword">
        <el-input
          v-model="form.newPassword"
          type="password"
          placeholder="请输入新密码"
          show-password
        />
      </el-form-item>
      
      <el-form-item label="确认新密码" prop="confirmPassword">
        <el-input
          v-model="form.confirmPassword"
          type="password"
          placeholder="请再次输入新密码"
          show-password
        />
      </el-form-item>
      
      <el-form-item>
        <el-button type="primary" @click="submitForm" :loading="loading">确认修改</el-button>
        <el-button @click="dialogVisible = false">取消</el-button>
      </el-form-item>
    </el-form>
  </el-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { ElMessage } from 'element-plus';
import request from '@/api/request';
import authState from '@/utils/authState';
import { useRouter } from 'vue-router';

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:visible', 'success']);
const router = useRouter();

// 弹窗显示状态
const dialogVisible = computed({
  get: () => props.visible,
  set: (val) => emit('update:visible', val)
});

// 表单引用
const formRef = ref(null);

// 表单数据
const form = ref({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
});

// 处理中状态
const loading = ref(false);

// 表单验证规则
const rules = {
  currentPassword: [
    { required: true, message: '请输入当前密码', trigger: 'blur' },
    { min: 6, message: '密码长度不能小于6个字符', trigger: 'blur' }
  ],
  newPassword: [
    { required: true, message: '请输入新密码', trigger: 'blur' },
    { min: 6, message: '密码长度不能小于6个字符', trigger: 'blur' }
  ],
  confirmPassword: [
    { required: true, message: '请再次输入新密码', trigger: 'blur' },
    {
      validator: (rule, value, callback) => {
        if (value !== form.value.newPassword) {
          callback(new Error('两次输入的密码不一致'));
        } else {
          callback();
        }
      },
      trigger: 'blur'
    }
  ]
};

// 清空表单数据
const resetForm = () => {
  form.value = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  };
  
  if (formRef.value) {
    formRef.value.resetFields();
  }
};

// 监听对话框关闭
watch(dialogVisible, (newVal) => {
  if (!newVal) {
    // 当对话框关闭时重置表单
    resetForm();
  }
});

// 提交表单
const submitForm = async () => {
  if (!formRef.value) return;
  
  try {
    await formRef.value.validate();
    loading.value = true;
    
    // 使用request工具发送修改密码请求
    try {
      await request.post('/users/change-password', {
        currentPassword: form.value.currentPassword,
        newPassword: form.value.newPassword
      });
      
      // 成功后清空表单
      resetForm();
      
      // 关闭对话框
      dialogVisible.value = false;
      
      // 触发成功事件
      emit('success');

      // 显示成功消息并提示用户重新登录
      ElMessage.success('密码修改成功，请重新登录');
      
      // 延迟执行登出操作，让用户有时间看到成功消息
      setTimeout(() => {
        // 执行登出操作
        authState.logout();
        
        // 重定向到登录页面
        router.push({
          path: '/login',
          query: { redirect: router.currentRoute.value.fullPath }
        });
      }, 1500); // 1.5秒后执行登出
      
    } catch (error) {
      console.error('密码修改请求失败:', error);
      
      // 根据错误类型提供友好的错误信息
      if (error.response && error.response.status === 401) {
        ElMessage.error('当前密码不正确，请确认后重试');
      } else {
        ElMessage.error(error.response?.data?.message || '修改密码失败，请重试');
      }
    }
  } catch (validationError) {
    console.error('表单验证失败:', validationError);
    ElMessage.warning('请正确填写所有必填项');
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
:deep(.change-password-dialog) {
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.change-password-dialog .el-dialog) {
  margin: 15vh auto !important;
  position: relative !important;
  display: flex !important;
  flex-direction: column !important;
}

:deep(.change-password-dialog .el-dialog__body) {
  padding: 20px !important;
}
</style> 